/**
 * ``AnnotationLabelLayer`` — the Meridian-owned MapLibre **symbol** layer that
 * renders each annotation's on-map label: the operator's text label + the
 * measurement readout (ADR-034 Inc 5; feature-spec §7 / §12 / D12).
 *
 * Per D12, Terra Draw (:mod:`@/map/use-annotation-draw`) owns the **geometry**
 * render; this layer owns the **text**. It sources a label ``Point`` per
 * annotation (the pure :func:`annotationsToLabelFeatures` — anchor + combined
 * text + per-annotation colour/size), placed below the anchor with a dark halo
 * so it reads on any basemap (light + dark; REQ-UI-040 / 046). The text is
 * formatted per the operator's live units (``units.distance`` /
 * ``units.coordinate_format``), so it re-renders when the active Profile's
 * units change.
 *
 * Mirrors the imperative attach/setData/detach lifecycle of the other map
 * layers (e.g. :file:`detection-layer.tsx`), guarded by ``isMapUsable``
 * (fail-safe; CLAUDE.md §8.1). WebGL-only — verified in the deferred §5.11
 * visual pass; the text/anchor logic it composes is unit-tested.
 *
 * Cross-references:
 *   - ADR-034; feature-spec §7 / §12 / D12. REQ-UI-035 (BFF-only — the glyph
 *     atlas is same-origin ``/api/glyphs``).
 *   - @/annotations/annotation-label (the pure source builder).
 */
import { useEffect, useMemo } from "react";
import type { GeoJSONSource, Map as MapLibreMap } from "maplibre-gl";
import type { FeatureCollection, Point } from "geojson";
import { isMapUsable } from "@/map/map-ready";
import { useAnnotationsWithDrag } from "@/annotations/annotation-drag-store";
import { usePreferences } from "@/preferences/preferences-context";
import { annotationsToLabelFeatures } from "@/annotations/annotation-label";

const SOURCE_ID = "meridian-annotation-labels-source";
const LAYER_ID = "meridian-annotation-labels-layer";

/** Attach the source + symbol layer on first call; ``setData`` thereafter. */
function attach(map: MapLibreMap, data: FeatureCollection<Point>): void {
  if (!isMapUsable(map)) {
    return;
  }
  const source = map.getSource(SOURCE_ID);
  if (source === undefined) {
    map.addSource(SOURCE_ID, { type: "geojson", data });
    map.addLayer({
      id: LAYER_ID,
      type: "symbol",
      source: SOURCE_ID,
      layout: {
        "text-field": ["get", "text"],
        // The BFF glyph atlas fontstack (issue #205); OFL-licensed UI body font.
        "text-font": ["DM Sans Medium"],
        "text-size": ["get", "size_px"],
        // Per-feature placement (#10): polygon/circle/line labels sit above the
        // shape, a marker label below — read off the source feature.
        "text-anchor": ["get", "text_anchor"],
        "text-offset": ["get", "text_offset"],
        "text-max-width": 14,
        // Always render the labels — never let symbol collision/placement hide
        // them. Fixes the labels-vanish-after-several-unit-switches bug (#14):
        // each unit change re-runs setData → symbol placement, and the
        // collision/fade state could end up hiding every label. Overlap is
        // acceptable for operator annotations.
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": ["get", "color"],
        // Dark halo so the (default white) label reads on any basemap.
        "text-halo-color": "#0f1117",
        "text-halo-width": 1.5,
      },
    });
  } else {
    (source as GeoJSONSource).setData(data);
  }
}

/** Remove the symbol layer + its source. */
function detach(map: MapLibreMap): void {
  if (!isMapUsable(map)) {
    return;
  }
  if (map.getLayer(LAYER_ID) !== undefined) {
    map.removeLayer(LAYER_ID);
  }
  if (map.getSource(SOURCE_ID) !== undefined) {
    map.removeSource(SOURCE_ID);
  }
}

/** Render the on-map annotation labels. Mount with the loaded map; renders null. */
export function AnnotationLabelLayer({ map }: { map: MapLibreMap }): null {
  const annotations = useAnnotationsWithDrag();
  const units = usePreferences().workingState.units;
  const distanceUnit = units?.distance ?? "nautical_miles";
  const coordinateFormat = units?.coordinate_format ?? "decimal_degrees";

  const data = useMemo(
    () => annotationsToLabelFeatures(annotations ?? [], distanceUnit, coordinateFormat),
    [annotations, distanceUnit, coordinateFormat],
  );

  // Detach on unmount / map change.
  useEffect(() => () => detach(map), [map]);
  // Attach + keep the source in sync with the labels.
  useEffect(() => {
    attach(map, data);
  }, [map, data]);

  return null;
}
